import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader, ArrowLeft, Share2, ExternalLink } from 'lucide-react';
import { getPublicVideoDetails, incrementVideoView } from '../services/api';
import ShareModal from '../components/ShareModal';
import VideoReaction from '../components/VideoReaction';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import './VideoDetailsPage.css';

const VideoDetailsPage = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching video:', videoId);
        const data = await getPublicVideoDetails(videoId);
        console.log('Video data:', data);
        
        setVideo(data);
        
        // Increment view count
        incrementVideoView(videoId).catch(console.error);
        
      } catch (err) {
        console.error('Error fetching video:', err);
        setError('Video not found or unavailable');
      } finally {
        setLoading(false);
      }
    };

    if (videoId) {
      fetchVideoData();
    }
  }, [videoId]);

  if (loading) {
    return (
      <div className="video-details-loading">
        <Loader className="spinner" size={48} />
        <p>{t.share?.loading || 'Loading...'}</p>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="video-details-error">
        <div className="error-icon">⚠️</div>
        <h2>{t.share?.videoNotFound || 'Video Not Found'}</h2>
        <p>{error || 'This video does not exist or is not available.'}</p>
        <button onClick={() => navigate('/gallery')} className="back-btn">
          <ArrowLeft size={20} />
          {t.share?.backToGallery || 'Back to Gallery'}
        </button>
      </div>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="video-details-page">
      {/* Header */}
      <div className="video-details-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={20} />
          {t.share?.back || 'Back'}
        </button>
        <div className="header-actions">
          <button onClick={() => setShowShare(true)} className="share-button">
            <Share2 size={20} />
            {t.share?.share || 'Share'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="video-details-content">
        {/* Video Player */}
        <div className="video-player-section">
          <div className="video-container">
            <video
              controls
			  controlsList="nodownload"
              autoPlay={false}
              poster={video.thumbnail_url}
              className="main-video"
            >
              <source src={video.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <VideoReaction videoId={video.id} showCounts />

          {/* Video Info */}
          <div className="video-info">
            <h1 className="video-title">{video.scene_name}</h1>
            
            <div className="video-meta">
              <div className="meta-item">
                <span className="meta-label">{t.share?.createdBy || 'Created by'}</span>
                <span className="meta-value">{video.user_name || 'Anonymous'}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">{t.share?.duration || 'Duration'}</span>
                <span className="meta-value">{formatDuration(video.duration)}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">{t.share?.views || 'Views'}</span>
                <span className="meta-value">{video.view_count || 0}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">{t.share?.uploaded || 'Uploaded'}</span>
                <span className="meta-value">{formatDate(video.created_at)}</span>
              </div>
            </div>

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <div className="video-tags">
                {video.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Prompt */}
            {video.prompt && (
              <div className="video-description">
                <h3>{t.share?.prompt || 'Prompt'}</h3>
                <p>{video.prompt}</p>
              </div>
            )}

            {/* Embed Section */}
            <div className="embed-section">
              <h3>{t.share?.embedVideo || 'Embed this video'}</h3>
              <p className="embed-description">
                {t.share?.embedDesc || 'Share this video on your website or blog'}
              </p>
              <button 
                onClick={() => setShowShare(true)}
                className="get-embed-btn"
              >
                <ExternalLink size={18} />
                {t.share?.getEmbedCode || 'Get Embed Code'}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar (Optional - Related Videos) */}
        <div className="video-sidebar">
          <div className="sidebar-section">
            <h3>{t.share?.aboutAnimLearn || 'About AnimLearn'}</h3>
            <p>
              {t.share?.aboutText || 'Create stunning educational animations with AI-powered tools.'}
            </p>
            <a 
              href="/"
              className="cta-link"
            >
              {t.share?.createYourOwn || 'Create Your Own'} →
            </a>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShare && (
        <ShareModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          videoId={video.id}
          sceneName={video.scene_name}
        />
      )}
    </div>
  );
};

export default VideoDetailsPage;
